import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { FileSignature } from 'lucide-react';

/**
 * Non-blocking notice that updated DPA terms are live and when they take effect
 * (DPA clause 16.2). Shown to the practice OWNER during the notice period only.
 *
 * Deliberately a banner, not an interstitial. Blocking a practice the moment new
 * terms are published would lock clinicians out of live case records over a
 * change they did not make, and would withhold the Controller's own data to
 * pressure acceptance. Notice first; the shell blocks the owner - and only the
 * owner - once the deadline passes (see derivePolicyGate).
 */
export function DpaNoticeBanner({
  practiceId,
  dueAt,
}: {
  practiceId: string;
  dueAt: number | null;
}): JSX.Element {
  const due =
    dueAt === null
      ? null
      : new Date(dueAt).toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });

  return (
    <div
      role="status"
      data-testid="dpa-notice-banner"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-watch/30 bg-watch-bg px-4 py-2 text-sm text-watch"
    >
      <span className="flex items-center gap-2">
        <FileSignature className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          The data processing agreement has been updated.
          {due !== null && <> Please review and accept it by {due}.</>}
        </span>
      </span>
      <Link
        to={`/practices/${practiceId}/dpa`}
        data-testid="dpa-notice-review"
        className="rounded-md border border-watch/30 px-3 py-1 font-medium hover:bg-watch-bg"
      >
        Review now
      </Link>
    </div>
  );
}
